import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate, Link } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { selectAuthLoading, selectAuthError } from '../store/slices/authSlice';

const SignupContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: ${({ theme }) => theme.colors.background.main};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex-direction: column;
  }
`;

const LogoSide = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 30%;
  background-color: #1A1625;
  padding: ${({ theme }) => theme.spacing.xl};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    width: 100%;
    padding: ${({ theme }) => theme.spacing.xl} ${({ theme }) => theme.spacing.md};
    padding-top: 60px;
    padding-bottom: 30px;
  }
`;

const ContentSide = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 70%;
  padding: ${({ theme }) => theme.spacing.xxl};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    width: 100%;
    padding: ${({ theme }) => theme.spacing.xl} ${({ theme }) => theme.spacing.md};
  }
`;

const SignupCard = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 450px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    max-width: 100%;
  }
`;

const Logo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  
  img {
    height: 60px;
    margin-bottom: ${({ theme }) => theme.spacing.md};
  }
`;

const CreatorText = styled.div`
  color: white;
  font-size: 18px;
  font-weight: 500;
  margin-top: 8px;
`;

const LogoText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  color: white;
  text-align: center;
  
  h3 {
    font-size: 14px;
    font-weight: 500;
    margin: 0;
    margin-bottom: 4px;
  }
  
  h2 {
    font-size: 20px;
    font-weight: 600;
    margin: 0;
  }
`;

const Title = styled.h1`
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 24px;
  font-weight: 600;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  align-self: flex-start;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    align-self: center;
    margin-top: ${({ theme }) => theme.spacing.xl};
  }
`;

const Subtitle = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 16px;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  align-self: flex-start;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    align-self: center;
    text-align: center;
    margin-bottom: ${({ theme }) => theme.spacing.lg};
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
`;

const FormRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  width: 100%;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing.lg};
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
  flex: 1;
`;

const Label = styled.label`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 14px;
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const Input = styled.input`
  background-color: ${({ theme }) => theme.colors.background.main};
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  padding: ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 16px;
  width: 100%;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const Button = styled.button`
  background-color: #9333EA; /* Using the purple color from the design */
  color: white;
  border: none;
  border-radius: 4px;
  padding: ${({ theme }) => theme.spacing.md};
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
  width: 100%;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.action.hover};
  }
  
  &:disabled {
    background-color: rgba(255, 255, 255, 0.1);
    cursor: not-allowed;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    margin-top: ${({ theme }) => theme.spacing.md};
    padding: 14px;
  }
`;

const ErrorMessage = styled.div`
  color: #ff5252;
  font-size: 14px;
  margin-top: ${({ theme }) => theme.spacing.md};
  text-align: center;
`;

const LoginText = styled.div`
  display: flex;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.xl};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 14px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    margin-top: ${({ theme }) => theme.spacing.xxl};
    position: relative;
    bottom: 0;
  }
`;

const LoginLink = styled(Link)`
  color: #9333EA; /* Using the purple color from the design */
  text-decoration: none;
  font-weight: 500;
  
  &:hover {
    text-decoration: underline;
  }
`;

const Checkbox = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.md};
  
  input {
    width: 20px;
    height: 20px;
    accent-color: #9333EA;
  }
  
  label {
    color: ${({ theme }) => theme.colors.text.secondary};
    font-size: 14px;
  }
`;

const TermsText = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 14px;
  margin-top: ${({ theme }) => theme.spacing.lg};
  text-align: center;
  
  a {
    color: #9333EA;
    text-decoration: none;
    font-weight: 500;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const Signup: React.FC = () => {
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [receiveUpdates, setReceiveUpdates] = useState(false);
  const [formError, setFormError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const loading = useAppSelector(selectAuthLoading);
  const error = useAppSelector(selectAuthError);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Form validation
    if (!phone || !email || !password) {
      setFormError('All fields are required');
      return;
    }
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setFormError('Please enter a valid email address');
      return;
    }
    
    // Validate phone number format (simple check for now)
    const phoneRegex = /^\+?[0-9]{10,15}$/;
    if (!phoneRegex.test(phone.replace(/\s+/g, ''))) {
      setFormError('Please enter a valid phone number');
      return;
    }
    
    // Validate password strength
    if (password.length < 8) {
      setFormError('Password must be at least 8 characters long');
      return;
    }
    
    setFormError('');
    setIsLoading(true);
    
    // TODO: Implement signup functionality when available in the authSlice
    // For now, simulate API call with timeout
    setTimeout(() => {
      setIsLoading(false);
      navigate('/verify', { 
        state: { phoneNumber: phone } 
      });
    }, 1000);
  };
  
  return (
    <SignupContainer>
      <LogoSide>
        <Logo>
          <img src="/creator-logo.svg" alt="BlinkLink Creator" />
        </Logo>
      </LogoSide>
      <ContentSide>
        <SignupCard>
          <Title>Welcome</Title>
          <Subtitle>Become an enterprise creator</Subtitle>
          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <Label htmlFor="phone">Phone number</Label>
              <Input
                id="phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+1 000 000 0012"
                required
              />
            </FormGroup>
            <FormGroup>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your Email address"
                required
              />
            </FormGroup>
            <FormGroup>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Create password"
                required
              />
            </FormGroup>
            
            <Checkbox>
              <input
                type="checkbox"
                id="receiveUpdates"
                checked={receiveUpdates}
                onChange={(e) => setReceiveUpdates(e.target.checked)}
              />
              <label htmlFor="receiveUpdates">Receive text updates from creator concierge</label>
            </Checkbox>
            
            <TermsText>
              by continuing you agree with app's <a href="/terms">T&C</a> and <a href="/privacy">Privacy Policy</a>
            </TermsText>
            {formError && <ErrorMessage>{formError}</ErrorMessage>}
            {error && <ErrorMessage>{error}</ErrorMessage>}
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Processing...' : 'Next'}
            </Button>
            <LoginText>
              Already have an account? <LoginLink to="/login">Login</LoginLink>
            </LoginText>
          </Form>
        </SignupCard>
      </ContentSide>
    </SignupContainer>
  );
};

export default Signup;
